import React, { useMemo } from 'react'
import Select, { GroupBase, InputActionMeta, Props, SelectInstance, components } from 'react-select'
import Creatable from 'react-select/creatable'
import cx from 'clsx'
import Tooltip from '@src/components/Tooltip'
import { twMerge } from 'tailwind-merge'

import { QuestionMarkCircleIcon } from '@heroicons/react/outline'
import { IconChevronDown, IconX } from '@tabler/icons-react'
import { getSelectStyles } from './utils'

export { useSelectHelpers } from './hooks/useSelectHelpers'

interface ReactSelectProps {
  label?: string | React.ReactElement
  errorMessage?: string
  tooltip?: string
  required?: boolean
  refObj?: React.RefObject<SelectInstance>
  classes?: {
    control?: string
    label?: string
    container?: string
    inputContainer?: string
    errorMessage?: string
  }
  isClearable?: boolean
  onCreateOption?: (value: string) => void
  handleInputChange?: (newValue: string, actionMeta: InputActionMeta) => void
  isValidNewOption?: (value: string) => boolean
}

export type Option = {
  label: string
  value: string
  [key: string]: unknown
}

export const ReactSelect = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  className,
  label,
  tooltip,
  errorMessage,
  required,
  classes,
  onCreateOption,
  handleInputChange,
  isValidNewOption,
  isClearable,
  components: selectComponents,
  ...props
}: Props<Option, IsMulti, Group> & ReactSelectProps) => {
  const styles = useMemo(() => getSelectStyles<Option, IsMulti, Group>(errorMessage), [errorMessage])

  const normalizedLabel = useMemo(() => {
    if (React.isValidElement(label) || typeof label === 'string' || typeof label === 'number') {
      return label
    }

    if (label && typeof label === 'object' && 'value' in label) {
      const value = (label as { value?: unknown }).value
      if (typeof value === 'string' || typeof value === 'number') {
        return value
      }
    }

    return ''
  }, [label])

  const tooltipId = `${props.name || props.id || 'select'}-${normalizedLabel}`
  const labelElement = normalizedLabel ? (
    <label className={cx('flex items-center mb-0.5 overflow-auto', classes?.label)}>
      {tooltip ? (
        <>
          <div data-tooltip-id={tooltipId} className="flex items-center">
            {normalizedLabel}
            <QuestionMarkCircleIcon className="h-3 ml-1" />
          </div>
          <Tooltip id={tooltipId}>
            <span>{tooltip}</span>
          </Tooltip>
        </>
      ) : (
        normalizedLabel
      )}
      {required && <span className="text-red-500">*</span>}
    </label>
  ) : null

  const Cmp = onCreateOption ? Creatable : Select

  return (
    <div
      className={twMerge(cx(!errorMessage && 'mb-[1rem]', 'notranslate', className))}
      translate="no"
      onClick={(e) => e.stopPropagation()}
    >
      <div className={cx(classes?.container)}>
        {labelElement}
        <Cmp<Option, IsMulti, Group>
          menuPortalTarget={document.body}
          components={{
            ...selectComponents,
            Control: ({ children, ...propsControl }) => (
              <components.Control
                {...propsControl}
                innerProps={
                  {
                    'data-qa': `${label || props.name || props.id}-select`,
                    ...propsControl.innerProps,
                  } as Record<string, unknown>
                }
              >
                {children}
              </components.Control>
            ),
            Menu: (menuProps) => (
              <components.Menu
                {...menuProps}
                innerProps={
                  {
                    ...menuProps.innerProps,
                    translate: 'no',
                    className: cx(
                      (menuProps.innerProps as { className?: string } | undefined)?.className,
                      'notranslate',
                    ),
                  } as React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
                }
              />
            ),
            MenuPortal: (portalProps) => (
              <components.MenuPortal
                {...portalProps}
                innerProps={
                  {
                    ...portalProps.innerProps,
                    translate: 'no',
                    className: cx(
                      (portalProps.innerProps as { className?: string } | undefined)?.className,
                      'notranslate',
                    ),
                  } as React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
                }
              />
            ),
            Option:
              selectComponents?.Option ||
              ((props) => {
                return (
                  <components.Option
                    {...props}
                    innerProps={
                      {
                        ...props.innerProps,
                        'data-qa': `${props.label}-option`,
                        translate: 'no',
                      } as React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
                    }
                  />
                )
              }),
            ClearIndicator: (props) => {
              return (
                components.ClearIndicator && (
                  <components.ClearIndicator {...props}>
                    <IconX className="text-neutral-300" stroke={1.5} size={16} />
                  </components.ClearIndicator>
                )
              )
            },
            DropdownIndicator: (props) => {
              return (
                components.DropdownIndicator && (
                  <components.DropdownIndicator {...props}>
                    <IconChevronDown className="text-neutral-300" stroke={1.5} size={16} />
                  </components.DropdownIndicator>
                )
              )
            },
          }}
          menuPlacement="auto"
          {...props}
          className={cx('rounded-base')}
          classNames={{
            container: () => cx(classes?.inputContainer),
            control: (propsControl) =>
              cx(classes?.control as string, propsControl.isDisabled ? '!bg-netural-50 !border-gray-300' : ''),
          }}
          onCreateOption={onCreateOption}
          onInputChange={handleInputChange}
          isValidNewOption={isValidNewOption}
          isClearable={isClearable}
          styles={{ ...styles, ...(props.styles || {}) }}
        />
      </div>
      {errorMessage && (
        <div className={cx('w-full mt-0.5 text-[11px] text-red-500', classes?.errorMessage)}>{errorMessage}</div>
      )}
    </div>
  )
}

export default ReactSelect
